import m from '@bitstillery/mithril'
import {
    Button,
    CollectionHeader,
    CollectionItems,
    CollectionSorter,
    CollectionStatus,
    CollectionView,
    CollectionViewToggle,
    Icon,
    PanelFilters,
} from '@bitstillery/common/components/portal'
import {MithrilComponent} from '@bitstillery/mithril'
import {state} from '@bitstillery/mithril'
import {CollectionProxy, build_transforms} from '@bitstillery/common/lib/collection'
import {activated_filters, generate_filters} from '@bitstillery/common/lib/filters'
import {format_money_with_symbol} from '@bitstillery/common/lib/format'
import {$t, store} from '@bitstillery/common/app'
import {classes, PlausibleCartSource, slugify} from '@bitstillery/common/lib/utils'
import {observe_shop_grid} from '@/lib/shop_grid_balance'
import {html_element} from '@bitstillery/common/lib/dom'
import type {
    AvailabilityStatistic,
    GetPortalOfferItemResponse,
    GetPortalOfferItemsResponse,
    PortalMenuCategory,
    StatisticsForFilterResponse,
} from '@bitstillery/common/api/fact2server'

import type {
    FilterDescription,
    FilterMultipleOption,
    FilterSelectMultipleState,
    FilterSelectSingleState,
    FilterTextState,
    FiltersDescription,
} from '@bitstillery/common/types'

import {CollectionProduct} from '@/components/collection/product'
import type {CollectionItemOptions} from '@/components/collection/product/product'
import {cart_bounce} from '@/lib/cart_bounce'
import {$m, $s} from '@/app'
import {panel_cart_peek_units} from '@/components/panels/panel_cart/panel_cart_state'
import {close_portal_shop_overlays} from '@/lib/shop_portal_overlays'
import {IncentivePersonalOffer} from '@/components/incentives'
import {PanelCategories} from '@/components/panels'
import {PricelistDownload} from '@/components/pricelist_download/pricelist_download'
import {OfferItemDetailDialog} from '@/components/pages/offers/lib/offer_item_detail_dialog'
import {PanelCart} from '@/components/panels/panel_cart/panel_cart'

type SingleOption = FilterSelectSingleState['options'][number]

interface OfferFilters extends FiltersDescription {
    alcohol: Partial<FilterSelectSingleState>
    availability: Partial<FilterSelectMultipleState>
    brand: Partial<FilterSelectSingleState>
    brand_holder: Partial<FilterSelectSingleState>
    category: FilterDescription<FilterSelectMultipleState>
    offertype: Partial<FilterSelectSingleState>
    offer: Partial<FilterSelectSingleState>
    packaging: Partial<FilterSelectMultipleState>
    gift_box_type: Partial<FilterSelectMultipleState>
    refill: Partial<FilterSelectSingleState>
    search: Partial<FilterTextState>
    volume: Partial<FilterSelectSingleState>
}

export const filters = generate_filters<OfferFilters>({
    alcohol: {
        count: true,
        icon: 'alcoholPercentage',
        placement: {order: 5},
        serialize: 'number',
        type: 'SELECT_SINGLE',
    },
    availability: {
        count: true,
        icon: 'availability',
        // @i18n ignore: filters.types.availability.*
        i18n: {prefix: 'filters.types.availability.'},
        placement: {order: 4},
        serialize: ['string'],
        type: 'SELECT_MULTIPLE',
    },
    brand: {
        count: true,
        icon: 'brands',
        placement: {order: 9},
        serialize: 'string',
        type: 'SELECT_SINGLE',
    },
    brand_holder: {
        count: true,
        icon: 'brandHolders',
        placement: {order: 10},
        serialize: 'string',
        type: 'SELECT_SINGLE',
    },
    category: {
        count: true,
        icon: 'bottle',
        // @i18n ignore: product_category.**
        i18n: {
            prefix: 'product_category.',
            label: () => (name: string) => slugify(name),
        },
        placement: {order: 3},
        serialize: ['number'],
        type: 'SELECT_MULTIPLE',
    },
    gift_box_type: {
        count: true,
        icon: 'promotion',
        // @i18n ignore: filters.types.gift_box_type.*
        i18n: {prefix: 'filters.types.gift_box_type.'},
        placement: {order: 6},
        serialize: ['string'],
        type: 'SELECT_MULTIPLE',
    },
    offer: {
        count: true,
        icon: 'offer',
        placement: {order: 0},
        serialize: 'string',
        type: 'SELECT_SINGLE',
    },
    offertype: {
        count: true,
        icon: 'specialOffers',
        // @i18n ignore: filters.types.offertype.*
        i18n: {prefix: 'filters.types.offertype.'},
        placement: {order: 1},
        serialize: 'string',
        type: 'SELECT_SINGLE',
    },
    packaging: {
        count: true,
        icon: 'packaging',
        // @i18n ignore: filters.types.packaging.*
        i18n: {prefix: 'filters.types.packaging.'},
        placement: {order: 7},
        serialize: ['string'],
        type: 'SELECT_MULTIPLE',
    },
    refill: {
        count: true,
        icon: 'refillable',
        // @i18n ignore: filters.types.refill.*
        i18n: {prefix: 'filters.types.refill.'},
        placement: {order: 8},
        serialize: 'string',
        type: 'SELECT_SINGLE',
    },
    search: {
        icon: 'search',
        serialize: ['string'],
        // Its own label: `filters.title.search` is the discover admin's, used by 29 of its collections,
        // and the name cannot change here — the collection reads `search` to route the value into
        // `search_terms`.
        title: 'filters.title.portal_search',
        type: 'TEXT',
    },
    volume: {
        count: true,
        icon: 'volume',
        placement: {order: 5},
        serialize: 'number',
        type: 'SELECT_SINGLE',
    },
})

type CategoryStat = [number, string, number, CategoryStat[]]

/**
 * The category counts from the last `portal/filter/statistics` response. Kept around because the
 * option *tree* comes from `$s.categories.menu` (fetched separately by `SearchSite`) while only the
 * counts come from the statistics call — on a cold shop load the two race, and whichever arrives
 * second has to rebuild the options. Without this the filter panel's category list stays empty until
 * an unrelated filter change triggers the next statistics fetch.
 */
let last_category_statistics: StatisticsForFilterResponse['category_statistics'] = []

export function apply_category_filter_options() {
    function format_category_stats(category: PortalMenuCategory): CategoryStat {
        const statistic = last_category_statistics?.find((i) => i.artkey === category.artkey)
        return [
            category.artkey,
            category.name,
            statistic ? statistic.count : 0,
            category.children.map((child) => format_category_stats(child)),
        ]
    }

    const menu = $s.categories.menu || []
    const category_stats = menu.map((category) => format_category_stats(category))
    for (const category of category_stats.filter((stat) => stat[2] === 0)) {
        category[2] = category[3].map((child) => child[2]).reduce((acc, value) => acc + value, 0)
    }
    filters.category.options.splice(0, filters.category.options.length, ...category_stats)
}

/** How the shop sorts. Configuration, not data — which is why the server can render the controls for it. */
const SORT = {
    by: 'product_name',
    options: [
        ['product_name', 'Product name'],
        ['list_quantity', 'List quantity'],
        ['delivery_period', 'Delivery period'],
        ['list_price', 'List price'],
        ['entry_date', 'In stock since'],
    ] as [string, string][],
    order: 'asc' as const,
}

const transforms = build_transforms<
    GetPortalOfferItemsResponse,
    GetPortalOfferItemResponse,
    StatisticsForFilterResponse,
    typeof filters
>({
    with_filter_metadata: {
        endpoint: 'portal/filter/statistics',
        transform: (result) => {
            last_category_statistics = result.category_statistics

            const availability_stats =
                result.availability_statistics?.map((i: AvailabilityStatistic): FilterMultipleOption => [
                    i.offer_item_type,
                    i.offer_item_type,
                    i.count,
                    i.children.map((child): FilterMultipleOption => [child.offer_item_type, child.offer_item_type, child.count]),
                ]) ?? []

            // Update the filter statistics.
            const percentage_unit = $t('unit.percentage')
            const alcohol_stats =
                result.alcohol_statistics?.map((i): SingleOption => [
                    i.alcohol_percentage,
                    `${i.alcohol_percentage}${percentage_unit}`,
                    i.count,
                ]) ?? []
            const packaging_stats = result.packaging_statistics?.map((i): FilterMultipleOption => [i.name, i.name, i.count]) ?? []
            const gift_box_stats = result.gift_box_statistics?.map((i): FilterMultipleOption => [i.name, i.name, i.count]) ?? []
            const offer_type_stats =
                result.offer_item_statistics?.map((i): SingleOption => [i.offer_item_type, i.offer_item_type, i.count]) ?? []

            const refill_stats =
                result.refill_status_statistics?.map((i): SingleOption => [i.refill_status, i.refill_status, i.count]) ?? []
            const volume_stats =
                result.volume_statistics?.map((i): SingleOption => [i.size_in_cl, `${i.size_in_cl} cl`, i.count]) ?? []
            const brand_holder_stats =
                result.brand_holder_statistics?.map((i): SingleOption => [`${i.artkey}`, i.name, i.count]) ?? []
            const brand_stats = result.brand_statistics?.map((i): SingleOption => [`${i.artkey}`, i.name, i.count]) ?? []
            const offer_stats =
                result.offer_statistics?.flatMap((i): SingleOption[] => (i.hash === null ? [] : [[i.hash, i.name, i.count]])) ??
                []

            // Update the filter options.
            filters.alcohol.options.splice(0, filters.alcohol.options.length, ...alcohol_stats)
            filters.availability.options.splice(0, filters.availability.options.length, ...availability_stats)
            apply_category_filter_options()
            filters.packaging.options.splice(0, filters.packaging.options.length, ...packaging_stats)
            filters.gift_box_type.options.splice(0, filters.gift_box_type.options.length, ...gift_box_stats)
            filters.offertype.options.splice(0, filters.offertype.options.length, ...offer_type_stats)
            filters.offer.options.splice(0, filters.offer.options.length, ...offer_stats)

            filters.refill.options.splice(0, filters.refill.options.length, ...refill_stats)
            filters.volume.options.splice(0, filters.volume.options.length, ...volume_stats)
            filters.brand_holder.options.splice(0, filters.brand_holder.options.length, ...brand_holder_stats)
            filters.brand.options.splice(0, filters.brand.options.length, ...brand_stats)
        },
    },
    with_filters_to_query: (query_filters) => ({
        alcohol_percentage: query_filters.alcohol.selection !== '' ? query_filters.alcohol.selection : undefined,
        availabilities: query_filters.availability.selection,
        brand: query_filters.brand.selection ? query_filters.brand.selection : undefined,
        brand_holder: query_filters.brand_holder.selection ? query_filters.brand_holder.selection : undefined,
        category_artkeys: query_filters.category.selection,
        gift_box_type: query_filters.gift_box_type.selection,
        limit: 50,
        offer_hash: query_filters.offer.selection ? query_filters.offer.selection : undefined,
        offer_item_type: query_filters.offertype.selection ? query_filters.offertype.selection : 'BROWSE_STOCK',
        packaging: query_filters.packaging.selection,
        refill_status: query_filters.refill.selection,
        search_terms: query_filters.search.selection,
        sort_ascending: collection.state.sort.order === 'asc',
        sort_by: collection.state.sort.by,
        volume: query_filters.volume.selection ? query_filters.volume.selection : undefined,
    }),
    with_items_queried: async ({result, status_code}) => {
        if (status_code === 404) {
            // The relation's price list isn't generated yet. The collection is flagged `poll_when_not_ready`,
            // so CollectionItems shows a waiting widget and polls until it's ready — no MessRoom needed.
            return []
        }

        if (status_code > 299) {
            return []
        }
        const response = result
        // #1927 - Temporarily disable COO. This code will self-destruct in 6 months.
        await $m.offer.load_product_photos(response.portal_offer_items.map((i) => i.artkey))
        return response.portal_offer_items
    },
})

export const collection = new CollectionProxy<GetPortalOfferItemResponse, typeof filters>()
export class OfferList extends MithrilComponent {
    data = (() =>
        state(
            {
                carousel_item: null,
            },
            'offer-list-data',
        ))()

    private item_renderer = (row: GetPortalOfferItemResponse, opts: CollectionItemOptions) => (
        <CollectionProduct
            collection={collection}
            global_index={opts.global_index}
            offer_item={row}
            on_item_height_change={opts.on_item_height_change}
            source={PlausibleCartSource.OFFERS}
            variant='offer'
            virtualize={true}
        />
    )

    private stop_grid_balance?: () => void

    override oncreate(vnode: m.VnodeDOM) {
        const grid = html_element(vnode.dom)
        if (grid) {
            this.stop_grid_balance = observe_shop_grid(grid)
        }
    }

    override onremove() {
        this.stop_grid_balance?.()
    }

    override oninit() {
        $s.panels.view.menu.collapsed = true
        $s.panels.view.categories.collapsed = true
        $s.panels.view.filters.collapsed = true

        collection.state.view.mode = $s.shop.collection_view_mode

        // The products load in the browser, not on the server — `collection.init` turns the collection
        // ready, which is what makes CollectionItems fetch. The shell around them is not data though:
        // handing the server the sort options and the filter set lets it render the toolbar and the
        // filter column as they will stay, so hydration fills the shop in rather than rearranging it.
        // The filters' options and counts come with the statistics call, client-side either way.
        if (globalThis.__SSR_MODE__) {
            Object.assign(collection.state.sort, SORT)
            // What `init` does with the configured sort. Without it the reset control renders on the
            // server as though the sort had been changed, and un-highlights itself on hydration.
            Object.assign(collection.state.sort.defaults, {by: SORT.by, order: SORT.order})
            Object.assign(collection.filters, filters)
            return
        }

        // Offertype is a special case; this filter is used to determine the page.
        void collection.init(
            {
                endpoint: {
                    method: 'post',
                    path: 'portal/offer-items',
                },
                lookup: {
                    enabled: false,
                    ignore_filters: [],
                },
                // The endpoint 404s while the relation's price list is still being generated; keep the
                // collection in a waiting state and poll instead of showing an empty shop.
                poll_when_not_ready: true,
                sort: SORT,
            },
            filters,
            transforms,
        )
    }

    private toggle_categories() {
        if (!$s.panels.view.categories.collapsed) {
            $s.panels.view.categories.collapsed = true
        } else {
            $s.panels.view.menu.collapsed = true
            $s.panels.view.filters.collapsed = true
            $s.panels.context.collapsed = true
            $s.panels.view.categories.collapsed = false
        }
    }

    private toggle_filters() {
        if (!$s.panels.view.filters.collapsed) {
            $s.panels.view.filters.collapsed = true
        } else {
            $s.panels.view.menu.collapsed = true
            $s.panels.view.categories.collapsed = true
            $s.panels.context.collapsed = true
            $s.panels.view.filters.collapsed = false
        }
    }

    private toggle_cart() {
        if (!$s.panels.context.collapsed) {
            $s.panels.context.collapsed = true
        } else {
            $s.panels.view.menu.collapsed = true
            $s.panels.view.filters.collapsed = true
            $s.panels.view.categories.collapsed = true
            $s.panels.context.collapsed = false
        }
    }

    view(_vn: m.Vnode): m.Children {
        const filters_open = !$s.panels.view.filters.collapsed
        const cart_open = !$s.panels.context.collapsed
        const categories_open = !$s.panels.view.categories.collapsed
        const any_overlay_open = filters_open || cart_open || categories_open
        const filter_count = activated_filters(collection)

        return (
            <div className='c-offer shop-grid'>
                <div
                    className={classes('shop-backdrop', {
                        'shop-backdrop-visible': any_overlay_open,
                    })}
                    onclick={() => close_portal_shop_overlays()}
                />

                <div
                    className={classes('shop-panel shop-panel-filters', 'shop-overlay shop-overlay-bottom', 'shop-overlay-left', {
                        'shop-overlay-open': filters_open,
                    })}
                >
                    <PanelFilters collection={collection} no_float={true}>
                        <IncentivePersonalOffer />
                    </PanelFilters>
                </div>

                <div
                    className={classes('shop-panel shop-panel-categories', 'shop-panel-categories--sheet-surface', {
                        'shop-overlay shop-overlay-bottom': true,
                        'shop-overlay-open': categories_open,
                    })}
                >
                    <PanelCategories
                        on_close={() => {
                            $s.panels.view.categories.collapsed = true
                        }}
                    />
                </div>

                <CollectionView
                    // Greying out the shop means "this offer link is stale" — a hash from a mail or a
                    // bookmark that no longer resolves. The offer filter's own options are the authority
                    // on that: they come from the statistics call, so they cover every offer the visitor
                    // can shop, personal or general. `$s.offers.personal` only holds offers linked to the
                    // relation, which disabled general ones like the pallet deals. Empty options means the
                    // statistics call hasn't landed yet, not that the selection is unknown.
                    inactive={Boolean(
                        filters.offer.selection &&
                        filters.offer.selection !== 'SPECIALS' &&
                        filters.offer.options.length &&
                        !filters.offer.options.some(([hash]) => hash === filters.offer.selection),
                    )}
                    mode={$s.shop.collection_view_mode}
                >
                    <CollectionHeader>
                        <div className='actions'>
                            <CollectionStatus
                                collection={collection}
                                loading={$t('collection.loading_products')}
                                virtualize={true}
                            />
                            <div className='shop-collection-controls'>
                                <PricelistDownload icon={'download'} model={$s.offers.export} with_filters={true} />
                                <CollectionSorter collection={collection} />
                                <CollectionViewToggle
                                    collection={collection}
                                    on_mode_change={(mode) => {
                                        $s.shop.collection_view_mode = mode
                                        void store.save()
                                    }}
                                />
                            </div>
                        </div>
                    </CollectionHeader>

                    <CollectionItems collection={collection} item={this.item_renderer} overscan_rows={2} virtualize={true} />
                </CollectionView>

                <PanelCart mode='shop' />

                <div className='shop-surface-toggles'>
                    <div className='shop-surface-toggles-tablet'>
                        <div className='shop-floating-toggles'>
                            {/* Tablet portrait only (CSS-hidden from breakpoint-30 up, where the
                                breadcrumb band fits the full category menu). */}
                            <div className='shop-categories-toggle'>
                                <Button
                                    active={categories_open}
                                    icon='categories'
                                    onclick={() => this.toggle_categories()}
                                    type='info'
                                />
                            </div>
                            <div className='shop-filters-toggle'>
                                <Button active={filters_open} icon='filter' onclick={() => this.toggle_filters()} type='info'>
                                    {filter_count > 0 ? (
                                        <span className='shop-cart-toggle-badge'>
                                            {filter_count > 999 ? '999+' : filter_count}
                                        </span>
                                    ) : null}
                                </Button>
                            </div>
                            <div className='shop-cart-toggle' {...cart_bounce}>
                                <Button active={cart_open} icon='cart' onclick={() => this.toggle_cart()} type='info'>
                                    {$m.cart.cart_units() > 0 ? (
                                        <span className='shop-cart-toggle-badge'>
                                            {$m.cart.cart_units() > 999 ? '999+' : $m.cart.cart_units()}
                                        </span>
                                    ) : null}
                                </Button>
                            </div>
                        </div>
                    </div>
                    <div className='shop-surface-toggles-desktop'>
                        <div className='shop-cart-toggle' {...cart_bounce}>
                            <Button active={cart_open} icon='cart' onclick={() => this.toggle_cart()} type='info'>
                                {$m.cart.cart_units() > 0 ? (
                                    <span className='shop-cart-toggle-badge'>
                                        {$m.cart.cart_units() > 999 ? '999+' : $m.cart.cart_units()}
                                    </span>
                                ) : null}
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Mobile category/filter toggles */}
                <div
                    className={classes('shop-bottom-bar', {
                        'shop-bottom-bar--cart-peek': $m.cart.cart_amount_validated() > 0 || $m.cart.booked_amount() > 0,
                    })}
                >
                    <Button
                        active={categories_open}
                        className='shop-bottom-bar-categories'
                        icon='categories'
                        onclick={() => this.toggle_categories()}
                        text={$t('panel_categories.title')}
                        type={categories_open ? 'info' : 'default'}
                    />
                    <Button
                        active={filters_open}
                        className='shop-bottom-bar-filters'
                        icon='filter'
                        onclick={() => this.toggle_filters()}
                        text={filter_count > 0 ? `Filters (${filter_count})` : 'Filters'}
                        type={filters_open ? 'info' : 'default'}
                    />
                    {$m.cart.cart_amount_validated() > 0 || $m.cart.booked_amount() > 0 ? (
                        <button
                            className={classes('shop-mobile-cart-peek', {active: cart_open})}
                            type='button'
                            onclick={() => this.toggle_cart()}
                            {...cart_bounce}
                        >
                            <Icon name='cart' size='s' type='unset' />
                            <span className='shop-mobile-cart-peek-count'>{panel_cart_peek_units()}</span>
                            <span className='shop-mobile-cart-peek-total'>
                                {format_money_with_symbol(
                                    $m.cart.total_amount_validated(),
                                    $s.identity.user.currency,
                                    $s.language,
                                )}
                            </span>
                        </button>
                    ) : null}
                </div>

                <OfferItemDetailDialog collection={collection} />
            </div>
        )
    }
}
